import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Loader, NotificationType } from "@transfr-inc/dashboard-components";
import { PageLayout } from "@transfr-inc/dashboard-components/layouts";

import { WarningMessages } from "@transfr-inc/dashboard-components";
import { GenericError } from "@components/utils/notification.constants";
import { services } from "@src/dependencies";
import { useNotification } from "@src/hooks";
import { useEditWarningModal } from "@transfr-inc/dashboard-components/hooks";
import { useApiRequest } from "@src/utils/http-client";

import { EditMenuDetailsModal } from "../detail/components/modals/edit-menu-details/edit-menu-details.modal";
import { CategoriesSection } from "./components/categories.section";
import { DeleteMenuModal } from "./components/menu-builder/modals/delete-menu/delete-menu.modal";
import { OrganizationsSection } from "./components/organizations.section";
import { MenuDetailPageHeader } from "./components/page.header";

import "./index.scss";

export const MenuDetail = () => {
  const { addNotification } = useNotification();
  const history = useHistory();
  const { state } = useLocation();
  const [errorDeleteModal, openErrorDeleteModal] = useEditWarningModal({
    title: WarningMessages.menu.title,
    message: WarningMessages.menu.delete,
    icon: ["fa-solid", "fa-clock-rotate-left"],
    iconColor: "danger-red",
  });

  const { data, breadcrumbLabel } = state;
  const { menuBuilderService } = services;

  const [editMenuDetailsModalOpen, setEditMenuDetailsModalOpen] = useState();
  const [deleteMenuModalOpen, setDeleteMenuModalOpen] = useState();
  const [loading, setLoading] = useState();
  const [menuData, setMenuData] = useState(data);

  const onEditMenuDetails = async () => {
    getMenuDetails();
  };

  const mergeCategoryData = (updatedCategoryData) => {
    let sortedMenuCategories = menuData.categories.sort((a, b) => {
      const orderA =
        updatedCategoryData.find(
          (category) => category.categoryId === a.categoryId,
        )?.orderNumber ?? 0;
      const orderB =
        updatedCategoryData.find(
          (category) => category.categoryId === b.categoryId,
        )?.orderNumber ?? 0;
      return orderA - orderB;
    });

    for (const sortedMenuCategory of sortedMenuCategories) {
      sortedMenuCategory.isVisible = updatedCategoryData.find(
        (category) => category.categoryId === sortedMenuCategory.categoryId,
      )?.isVisible;
    }

    const updatedState = { ...menuData, categories: sortedMenuCategories };
    setMenuData(updatedState);
  };

  const { sendRequest: getMenuDetails, loading: menuLoading } = useApiRequest(
    () =>
      menuBuilderService
        .getMenuDetails(data.id)
        .then((response) => setMenuData(response))
        .catch(() => {
          addNotification({
            type: NotificationType.error,
            message: GenericError,
          });
        }),
    false,
  );

  useEffect(() => {
    if (data) {
      getMenuDetails();
    }
  }, []);

  const onDeleteMenu = async () => {
    setLoading(true);
    try {
      await menuBuilderService.deleteTemplateCategory(data.id);
      addNotification({
        type: NotificationType.success,
        message: "Menu successfully deleted",
      });
      history.push("/app/templates");
    } catch {
      addNotification({
        type: NotificationType.error,
        message: GenericError,
      });
    } finally {
      setLoading();
    }
  };

  const handleDelete = (isError) => {
    if (isError) {
      openErrorDeleteModal();
    } else {
      setDeleteMenuModalOpen(true);
    }
  };

  return menuData ? (
    <PageLayout
      className="menu-detail-page"
      header={
        <MenuDetailPageHeader
          menu={menuData}
          breadcrumb={breadcrumbLabel}
          onEditMenuClick={() => setEditMenuDetailsModalOpen(true)}
          onDeleteMenuClick={handleDelete}
        />
      }
    >
      {(loading || menuLoading) && <Loader overlay></Loader>}
      <div className="menu-detail-body">
        <div className="text-with-info">
          <label>
            Changes made will be automatically propagated to organizations
            provisioned with this menu<sup>*</sup>
          </label>
        </div>
        <OrganizationsSection menu={menuData}></OrganizationsSection>
        <CategoriesSection
          menu={menuData}
          mergeCategoryData={mergeCategoryData}
        ></CategoriesSection>
      </div>
      {editMenuDetailsModalOpen && (
        <EditMenuDetailsModal
          menu={menuData}
          open={editMenuDetailsModalOpen}
          onEditMenuDetailsHandler={onEditMenuDetails}
          onCancel={() => setEditMenuDetailsModalOpen()}
        />
      )}
      <DeleteMenuModal
        open={deleteMenuModalOpen}
        onCancel={() => setDeleteMenuModalOpen()}
        onDeleteMenu={onDeleteMenu}
        title={data?.name}
        setNotification={(type, message) => addNotification({ type, message })}
      />
      {errorDeleteModal}
    </PageLayout>
  ) : (
    <Loader overlay></Loader>
  );
};
